import { useEffect, useState, type MouseEvent } from 'react'
import { Link } from 'react-router-dom'
import { Markdown } from '@/components/Markdown'
import { Picker } from '@/components/Picker'
import { Section } from '@/components/Section'
import { TaskRows } from '@/components/TaskRows'
import { api } from '@/api/client'
import { wordDiff } from '@/lib/diff'
import { validate } from '@/lib/schema'
import { INTAKE_API } from '@/lib/templateIntakes'
import { PersonName } from '@/components/PersonName'
import type { Person, Task } from '@/lib/types'
import './scientific.css'

type Proposal = { id: string; title: string; brief: string
  /** Written for the authoring agents: the expert copy of the packet does not carry it. */
  notes?: string
  summary?: string; confidence?: string; estimated_tasks?: number }
type Research = {
  job_id?: string
  reviewer?: { peer_email?: string; peer?: Person | null; can_claim: boolean; can_review: boolean; can_release: boolean; can_retry: boolean }
  enabled: boolean; status: string; review_url?: string; blocker?: string; overview?: string; research?: string
  proposals?: Proposal[]
  approval?: { status: string; error?: string }
  /** The workflows approval created, as list rows, with their current stage. */
  workflows?: Task[]
  cancelled_approvals?: { job_id: string; error?: string; created: Record<string, string> }[]
}

/** Matches template_research_start.schema.json; the textarea stops longer pastes client-side. */
const MAX_DOCUMENT = 100000
/** wordDiff is an O(n·m) table, so it only ever sees the changed lines, bounded by this. */
const DIFF_PREVIEW = 12000

/** The lines that differ, with the common first and last lines dropped.
 *
 * A proposal brief runs to tens of thousands of characters; word-diffing a whole one would
 * build a table of billions of cells. Trimming first keeps a one-sentence edit instant.
 */
function changedLines(before: string, after: string) {
  const a = before.split('\n')
  const b = after.split('\n')
  let head = 0
  while (head < a.length && head < b.length && a[head] === b[head]) head++
  let tail = 0
  while (tail < a.length - head && tail < b.length - head && a[a.length - 1 - tail] === b[b.length - 1 - tail]) tail++
  return { before: a.slice(head, a.length - tail).join('\n'), after: b.slice(head, b.length - tail).join('\n'),
    trimmed: head > 0 || tail > 0 }
}

/** One packet document: read as Markdown, edited as Markdown, with its edits shown as a diff.
 *
 * Only the toggle is local; the text lives with the correction so a reload or the 15 s poll
 * cannot drop what the reviewer wrote.
 */
function Document({ label, source, text, editable, busy, documentLinks, onChange }: {
  label: string; source: string; text?: string; editable: boolean; busy: boolean
  documentLinks?: Record<string, string>; onChange: (text?: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const current = text ?? source
  const changed = current !== source
  const region = changed ? changedLines(source, current) : null
  const ops = region && region.before.length <= DIFF_PREVIEW && region.after.length <= DIFF_PREVIEW
    ? wordDiff(region.before, region.after) : null
  return <div className="md-doc">
    {editable && <div className="row md-doc-head">
      <button type="button" className="link" disabled={busy} onClick={() => setEditing(on => !on)}>
        {editing ? `Preview ${label}` : `Edit ${label}`}</button>
      {changed && <><span className="chip warn">Edited</span>
        <button type="button" className="link" disabled={busy} onClick={() => onChange(undefined)}>Undo {label} edits</button></>}
    </div>}
    {editing
      ? <label className="field"><span className="visually-hidden">{`${label} Markdown`}</span>
        <textarea className="md-source" value={current} maxLength={MAX_DOCUMENT} disabled={busy}
          onChange={event => onChange(event.target.value)} /></label>
      : <Markdown source={current} documentLinks={documentLinks} />}
    {!editing && changed && (ops
      ? <><pre className="diff" aria-label={`Changes to ${label}`}>{ops.map((part, index) =>
        part.kind === 'add' ? <ins key={index}>{part.text}</ins>
          : part.kind === 'del' ? <del key={index}>{part.text}</del> : <span key={index}>{part.text}</span>)}</pre>
        {region?.trimmed && <p className="small muted">Only the changed lines are shown. The whole document is sent as a diff.</p>}</>
      : <p className="small muted">This document is too long to preview word by word. Your edits are still sent to the researcher as a diff.</p>)}
  </div>
}

const QUEUED = 'This data is queued for research. Proposed workflows will appear here when they are ready.'
const researchStatus: Record<string, string> = {
  not_started: 'Research has not started yet.', pending_delivery: QUEUED, submitted: QUEUED, queued: QUEUED,
  running: 'Proposed workflows are being prepared from this data. They will appear here when they are ready.',
  succeeded: 'Proposed workflows are ready for review.',
  blocked: 'Research is paused and needs an answer.', blocked_expert: 'Research is waiting for an answer from an expert.',
  blocked_dev: 'Research stopped on a technical problem that a developer needs to fix.', failed: 'Research failed.',
  cancelled: 'Research was cancelled.', rejected: 'The research request was rejected.',
  unavailable: 'The research service is unavailable. Retrying automatically.',
}
const preparationStatus: Record<string, string> = {
  queued: 'The workflows are queued to be built.', running: 'The workflows are being prepared for their builders.',
  completed: 'The workflows are being built.', failed: 'Preparing the workflows for their builders failed.',
  cancelled: 'Preparing the workflows was cancelled.',
}
/** A researcher that stopped before it could finish; a developer can restart it with a message. */
const STOPPED = ['blocked', 'blocked_dev', 'blocked_expert', 'failed', 'cancelled', 'rejected']

export function ResearchReview({ id, revision, refresh, developer = false, sourceChanged = false, externalBusy = false, beforeDecision, onBusyChange }: {
  id: string; revision: number; developer?: boolean; refresh: () => Promise<void>; sourceChanged?: boolean; externalBusy?: boolean
  beforeDecision?: (action: string) => Promise<number>; onBusyChange?: (busy: boolean) => void
}) {
  const [value, setValue] = useState<Research | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [authoringMessage, setAuthoringMessage] = useState('')
  const [feedback, setFeedback] = useState<Record<string, string>>({})
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [direction, setDirection] = useState('')
  const [excluded, setExcluded] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [generation, setGeneration] = useState(0)
  const [open, setOpen] = useState('')
  const [tab, setTab] = useState('')
  const base = `${INTAKE_API}/${id}/research`
  async function read() {
    const result = await api.get<Research>(base)
    if (!result.review_url) return result
    const response = await fetch(result.review_url)
    if (!response.ok) throw new Error('The research packet could not be loaded. Reload to try again.')
    const packet = await response.json()
    return { ...result, overview: packet.overview, research: packet.research, proposals: packet.proposals } as Research
  }
  // Source autosaves advance the intake revision without replacing the proposal packet.
  useEffect(() => { setFeedback({}); setEdits({}); setExcluded([]); setDirection(''); setOpen(''); setTab('') }, [base, value?.job_id])
  async function reload() {
    setBusy(true); onBusyChange?.(true)
    try { await refresh(); setGeneration(n => n + 1) }
    catch (e) { setError((e as Error).message) }
    finally { setBusy(false); onBusyChange?.(false) }
  }
  async function load() { setValue(await read()); setLoaded(true) }
  useEffect(() => {
    let disposed = false
    let settled = false
    const update = () => read().then(result => { if (!disposed) { setValue(result); setLoaded(true); setError(''); settled = result.approval ? ['completed', 'failed'].includes(result.approval.status) : ['succeeded', 'failed', 'cancelled', 'rejected', 'not_started'].includes(result.status) } })
      .catch(e => { if (!disposed) { setError(e.message); setLoaded(true) } })
    void update()
    const timer = window.setInterval(() => { if (!settled) void update() }, 15000)
    return () => { disposed = true; window.clearInterval(timer) }
  }, [base, revision, generation])
  async function act(path: string, body: object) {
    setBusy(true); onBusyChange?.(true); setError('')
    try {
      const latestRevision = beforeDecision && ['', '/approve', '/claim', '/release'].includes(path) ? await beforeDecision(path) : revision
      await api.post(base + path, { ...body, ...('revision' in body ? { revision: latestRevision } : {}) })
      await refresh(); await load(); setGeneration(n => n + 1); setMessage(''); setAuthoringMessage(''); setFeedback({}); setEdits({})
    }
    catch (e) { setError((e as Error).message) }
    finally { setBusy(false); onBusyChange?.(false) }
  }
  const proposals = value?.proposals ?? []
  const selected = proposals.filter(item => !excluded.includes(item.id))
  const disabled = busy || externalBusy
  const forwarding = value?.status === 'succeeded' && direction === 'forward'
  const returning = value?.status !== 'succeeded' || direction === 'return'
  const sources: Record<string, string> = { ...(value?.overview ? { overview: value.overview } : {}) }
  for (const proposal of proposals) {
    sources[`brief:${proposal.id}`] = proposal.brief
    if (proposal.notes !== undefined) sources[`notes:${proposal.id}`] = proposal.notes
  }
  const labels: Record<string, string> = { overview: 'overview',
    ...Object.fromEntries(proposals.flatMap(p => [[`brief:${p.id}`, `${p.title} brief`], [`notes:${p.id}`, `${p.title} notes`]])) }
  const edited = Object.entries(edits).filter(([name, text]) => name in sources && text !== sources[name])
    .map(([document, text]) => ({ document, text }))
  const setEdit = (name: string) => (text?: string) => setEdits(({ [name]: _drop, ...rest }) =>
    text === undefined ? rest : { ...rest, [name]: text })
  const commented = proposals.filter(item => feedback[item.id]?.trim())
  async function correct() {
    const body = { revision, ...(message.trim() ? { message } : {}), ...(edited.length ? { edits: edited } : {}),
      ...(commented.length ? { workflow_feedback: Object.fromEntries(commented.map(item => [item.id, feedback[item.id]])) } : {}) }
    const problems = validate('template_research_start', body)
    if (problems.length) { setError(problems.map(problem => `${problem.path}: ${problem.message}`).join(' ')); return }
    await act('', body)
  }
  async function approve() {
    const workflowFeedback = Object.fromEntries(commented.filter(item => !excluded.includes(item.id)).map(item => [item.id, feedback[item.id]]))
    const body = { revision, workflow_ids: selected.map(item => item.id),
      ...(authoringMessage.trim() ? { message: authoringMessage } : {}),
      ...(Object.keys(workflowFeedback).length ? { workflow_feedback: workflowFeedback } : {}) }
    await act('/approve', body)
  }
  const correction = value && ['succeeded', 'failed', 'cancelled', 'rejected', 'blocked', 'blocked_dev', 'blocked_expert'].includes(value.status)
  // Editing exists to produce a correction, so it appears exactly where a correction can be sent.
  const decision = !!(correction && value?.reviewer?.can_review && !value?.approval)
  const index = proposals.findIndex(proposal => proposal.id === open)
  const proposal = index >= 0 ? proposals[index] : null
  function show(proposalId: string, section = '') { setOpen(proposalId); setTab(section) }
  // The overview's relative links to a proposal brief select that proposal instead of leaving the page.
  function follow(event: MouseEvent) {
    const href = (event.target as HTMLElement).closest('a')?.getAttribute('href')
    const picked = href?.startsWith('#research-proposal-') ? href.slice('#research-proposal-'.length) : null
    if (picked && proposals.some(item => item.id === picked)) { event.preventDefault(); show(picked) }
  }
  const proposalLinks = Object.fromEntries(proposals.flatMap(item =>
    [`workflows/${item.id}/brief.md`, `./workflows/${item.id}/brief.md`].map(path => [path, `#research-proposal-${item.id}`])))
  // Notes are written for the authoring agents, so only developers get that tab; nobody gets two
  // tabs where there is one document.
  const tabs: [string, string][] = proposal
    ? developer && proposal.notes !== undefined ? [['', 'Proposal brief'], ['notes', 'Notes for the authoring agents']] : []
    : value?.research ? [['', 'Overview'], ['research', 'How these proposals were made']] : []
  const current = tabs.some(([key]) => key === tab) ? tab : ''
  const statusText = value && (value.approval
    ? preparationStatus[value.approval.status] ?? `Workflow preparation: ${value.approval.status}`
    : researchStatus[value.status] ?? `Research: ${value.status.replaceAll('_', ' ')}`)
  const ready = !!value && (!!value.overview || !!proposals.length)
  const reviewer = value?.reviewer
  // The panel also carries the review assignment, so it shows for anyone who can claim, decide or release.
  const panel = ready && (decision || !!reviewer?.can_claim || !!reviewer?.can_release || !!reviewer?.peer_email)
  const notices = <>
    {error && <div className="note err" role="alert"><p>{error}</p><button disabled={disabled} onClick={() => void reload()}>Reload research</button></div>}
    {value?.blocker && <p className="note">{value.blocker}</p>}
    {developer && !decision && value && !value.approval && STOPPED.includes(value.status) && <form className="note" onSubmit={e => { e.preventDefault(); void act('', { revision, message }) }}>
      <label className="field"><span>Message to restart the researcher</span><textarea value={message} maxLength={20000} disabled={disabled} onChange={e => setMessage(e.target.value)} /></label>
      <button disabled={disabled || !message.trim()}>Restart research</button>
    </form>}
  </>
  const assignment = (reviewer?.peer_email || reviewer?.can_release) && <div className="science-review-assignment">
    {reviewer.peer_email && <span className="small muted">Reviewer: <PersonName people={reviewer.peer ? { [reviewer.peer.user_id]: reviewer.peer } : undefined}
      id={reviewer.peer?.user_id} email={reviewer.peer_email} /></span>}
    {reviewer.can_release && <button className="link" disabled={disabled || !!error} onClick={() => void act('/release', { revision })}>Release review</button>}
  </div>
  return <div className="science-research-review">
    {/* Until proposals exist there is only a status to give, so it gets one plain card. */}
    {/* Once the workflows exist, their list below is the template's progress. */}
    {!ready && value?.approval?.status !== 'completed' && <section className="box science-research-waiting" aria-label="Research progress">
      {notices}
      {/* A failed or empty read must not leave a permanent "Loading research…". */}
      {!value ? error
        ? <p className="muted">Research could not be loaded.</p>
        : loaded ? <p className="muted">No research record for this template yet.</p>
        : <p className="muted">Loading research…</p>
        : <p role="status">{value.enabled || value.approval ? statusText : 'Research is not available in this environment.'}</p>}
      {value?.enabled && (developer || reviewer?.can_review) && value.status === 'not_started' && <button disabled={disabled || !!error} onClick={() => void act('', { revision })}>Start research</button>}
      {assignment}
    </section>}
    {ready && <>
      <header className="science-research-head">
        <h2>{proposals.length === 1 ? 'One proposed workflow from this data'
          : proposals.length ? `${proposals.length} proposed workflows from this data` : 'Proposed workflows from this data'}</h2>
      </header>
      {notices}
      <section className="science-toolbar">
        <Picker id="research-proposal" field label="Proposal" value={open} disabled={disabled} onChange={picked => show(picked)}
          options={[{ value: '', label: 'All proposals', detail: `${proposals.length} proposal${proposals.length === 1 ? '' : 's'}` },
            ...proposals.map((item, position) => ({ value: item.id, label: item.title,
              detail: `${position + 1} of ${proposals.length}${item.estimated_tasks ? ` · ${item.estimated_tasks} tasks` : ''}` }))]} />
      </section>
      <div className={`science-review-layout peer-review-layout${panel ? ' with-decision' : ''}`}>
        <section className="science-materials">
          {tabs.length > 1 && <nav className="peer-review-tabs science-material-tabs" aria-label="Proposal documents">
            {tabs.map(([key, label]) => <button key={key} aria-selected={current === key} aria-current={current === key ? 'page' : undefined}
              onClick={() => setTab(key)}>{label}</button>)}</nav>}
          {proposal ? <section className="box">
            <h2>{proposal.title}</h2>
            <p className="small muted">Proposal {index + 1} of {proposals.length}
              {proposal.estimated_tasks ? ` · ${proposal.estimated_tasks} initial tasks` : ''}
              {proposal.confidence ? ` · ${proposal.confidence} confidence` : ''}</p>
            {current === 'notes' && proposal.notes !== undefined
              ? <Document label={`${proposal.title} notes`} source={proposal.notes} text={edits[`notes:${proposal.id}`]}
                editable={decision} busy={disabled} onChange={setEdit(`notes:${proposal.id}`)} />
              : <Document label={`${proposal.title} brief`} source={proposal.brief} text={edits[`brief:${proposal.id}`]}
                editable={decision} busy={disabled} onChange={setEdit(`brief:${proposal.id}`)} />}
            {decision && <label className="field"><span>Feedback on this workflow</span>
              <textarea value={feedback[proposal.id] ?? ''} maxLength={20000} disabled={disabled}
                onChange={e => setFeedback(current => ({ ...current, [proposal.id]: e.target.value }))} /></label>}
            <div className="row">
              <button className="ghost" disabled={disabled} onClick={() => show(index > 0 ? proposals[index - 1].id : '')}>
                {index > 0 ? `Previous: ${proposals[index - 1].title}` : 'Back to all proposals'}</button>
              {index + 1 < proposals.length && <button className="ghost" disabled={disabled} onClick={() => show(proposals[index + 1].id)}>
                Next: {proposals[index + 1].title}</button>}
            </div>
          </section> : <section className="box">
            <h2>{current === 'research' ? 'How these proposals were made' : 'Overview'}</h2>
            {/* research.md is the agent's record of the work it did, not a specification: it is read-only. */}
            {current === 'research' ? <Markdown source={value.research ?? ''} />
              : <div onClick={follow}>
                <p className="small muted">Open a proposal from the list above, or from the table below.</p>
                {value.overview && <Document label="overview" source={value.overview} text={edits.overview} documentLinks={proposalLinks}
                  editable={decision} busy={disabled} onChange={setEdit('overview')} />}
              </div>}
          </section>}
        </section>
        {panel && <div id="science-decision"><section className="box peer-decision" aria-label="Review decision">
          <h2>{decision || reviewer?.can_claim ? 'Your decision' : 'Review'}</h2>
          {reviewer?.can_claim && <>
            <p className="small muted">Claim this review to decide on these workflows.</p>
            <button className="primary" disabled={disabled || !!error} onClick={() => void act('/claim', { revision })}>Claim review</button>
          </>}
          {decision && <>
            {value.status === 'succeeded' && <div className="field research-next-step"><span>Next step</span>
              <Picker field label="Next step" value={direction} disabled={disabled} onChange={setDirection}
                options={[{ value: 'forward', label: 'Move to authoring', detail: 'Build the workflows you choose' },
                  { value: 'return', label: 'Send back to researcher', detail: 'Revise this proposal set' }]} />
            </div>}
            {forwarding && <>
              <div className="field"><span>Workflows to author</span>
                <Picker field multiple label="Workflows to author" value={selected.map(item => item.id)} disabled={disabled}
                  onChange={ids => setExcluded(proposals.filter(item => !ids.includes(item.id)).map(item => item.id))}
                  triggerContent={!selected.length ? 'Choose workflows…' : selected.length === proposals.length
                    ? proposals.length === 1 ? '1 workflow' : `All ${proposals.length} workflows` : `${selected.length} of ${proposals.length} workflows`}
                  options={proposals.map(item => ({ value: item.id, label: item.title,
                    detail: item.estimated_tasks ? `${item.estimated_tasks} initial tasks` : undefined }))} />
              </div>
              <p className="small muted">Only the selected workflows will start authoring.</p>
              <label className="field"><span>Comments for authoring agents (optional)</span>
                <textarea value={authoringMessage} disabled={disabled} maxLength={20000} onChange={event => setAuthoringMessage(event.target.value)} /></label>
              {commented.some(item => !excluded.includes(item.id)) && <p className="small muted" role="status">Your feedback on selected workflows will also be shared with their authoring agents.</p>}
              {!!edited.length && <p className="small muted">Edits reach the researcher only when you send them back. Authoring uses the submitted proposals.</p>}
            </>}
            {returning && <>
              <p className="small muted">The researcher will revise the proposal set using your feedback.</p>
              <label className="field"><span>Feedback for the researcher</span><textarea value={message} disabled={disabled} maxLength={20000} onChange={e => setMessage(e.target.value)} /></label>
              {!!commented.length && <div role="status"><p className="small muted">Feedback on {commented.length === 1 ? 'one workflow' : `${commented.length} workflows`}:</p>
                <ul className="small muted">{commented.map(item => <li key={item.id}><button type="button" className="link" onClick={() => show(item.id)}>{item.title}</button></li>)}</ul></div>}
              {!!edited.length && <div role="status"><p className="small muted">{edited.length === 1 ? 'One edited document is' : `${edited.length} edited documents are`} sent with this request as diffs:</p>
                <ul className="small muted">{edited.map(item => <li key={item.document}>{labels[item.document]}</li>)}</ul></div>}
            </>}
            {(forwarding || returning) && sourceChanged && <p className="small muted">Your updated template explanation and files will be included.</p>}
            <div className="research-decision-actions">
              {returning && <button className="primary" disabled={disabled || !!error || (!message.trim() && !edited.length && !commented.length && !sourceChanged)} onClick={() => void correct()}>Send back to researcher</button>}
              {forwarding && <button className="primary" disabled={disabled || !!error || !selected.length}
                onClick={() => void approve()}>Start authoring</button>}
            </div>
          </>}
          {assignment}
        </section></div>}
      </div>
    </>}
    {/* Once preparation completes, the workflows list below says everything; until then, or when it stops, this box does. */}
    {!!value && (value.approval && value.approval.status !== 'completed' || !!value.cancelled_approvals?.length) && <section className="box science-research-outcome" aria-label="Workflow preparation">
      {value.approval && value.approval.status !== 'completed' && <p role="status">{statusText}</p>}
      {value.approval?.error && <p className="note err">{value.approval.error}</p>}
      {value.reviewer?.can_retry && value.approval?.status === 'failed' && <div className="row">
        <button disabled={disabled || !!error} onClick={() => void act('/retry', {})}>Retry preparation</button>
        {/* Retrying cannot fix a broken packet or a revoked approver; cancelling reopens the review. */}
        <button disabled={disabled || !!error} onClick={() => void act('/cancel', { revision })}>Cancel preparation and reopen review</button>
      </div>}
      {value.cancelled_approvals?.some(c => Object.keys(c.created).length) && <div className="note">
        <p>Workflows already created by cancelled preparation:</p>
        <ul>{value.cancelled_approvals.flatMap(c => Object.entries(c.created)).map(([wid, tid]) => <li key={tid}><Link to={`/${developer ? 'eng' : 'sme'}/tasks/${tid}`}>{wid}</Link></li>)}</ul>
      </div>}
    </section>}
    {!!value?.workflows?.length && <Section title="Workflows from this data" count={value.workflows.length}>
      <TaskRows tasks={value.workflows} role={developer ? 'engineer' : 'sme'} showTodo hideSource empty="" />
    </Section>}
  </div>
}
